import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Send, Loader2, File, Folder, ChevronRight, ChevronDown, Code, Save, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectWorkspace = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [currentFile, setCurrentFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `project-${projectId}-${Date.now()}`);
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  useEffect(() => {
    loadProject();
    loadProjectFiles();
    loadChatHistory();
  }, [projectId]);

  const loadProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      console.error('Error loading project:', error);
      toast.error('Ошибка загрузки проекта');
    }
  };

  const loadProjectFiles = async (path = '') => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/files`, {
        params: { directory: path }
      });
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Error loading files:', error);
    }
  };

  const loadChatHistory = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/chat/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const openFile = async (filePath) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/file`, {
        params: { path: filePath }
      });
      setCurrentFile(filePath);
      setFileContent(response.data.content || '');
    } catch (error) {
      console.error('Error opening file:', error);
      toast.error('Ошибка открытия файла');
    }
  };

  const saveFile = async () => {
    if (!currentFile) return;
    
    try {
      await axios.post(`${API}/projects/${projectId}/file`, null, {
        params: {
          path: currentFile,
          content: fileContent
        }
      });
      toast.success('Файл сохранен');
    } catch (error) {
      console.error('Error saving file:', error);
      toast.error('Ошибка сохранения файла');
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/chat`,
        {
          session_id: sessionId,
          content: inputMessage,
          project_id: projectId
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setMessages(prev => [...prev, response.data]);
      
      // Перезагружаем файлы после выполнения действий
      if (response.data.metadata?.actions_executed?.length > 0) {
        loadProjectFiles();
        toast.success(`Выполнено действий: ${response.data.metadata.actions_executed.length}`);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Ошибка отправки сообщения');
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Извините, произошла ошибка. Попробуйте еще раз.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const renderFileTree = (filesList) => {
    return filesList.map((file, index) => (
      <div key={index}>
        {file.type === 'directory' ? (
          <div>
            <div 
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-white/5 cursor-pointer rounded"
              onClick={() => {
                const newExpanded = new Set(expandedFolders);
                if (newExpanded.has(file.path)) {
                  newExpanded.delete(file.path);
                } else {
                  newExpanded.add(file.path);
                }
                setExpandedFolders(newExpanded);
              }}
            >
              {expandedFolders.has(file.path) ? 
                <ChevronDown className="w-4 h-4" /> : 
                <ChevronRight className="w-4 h-4" />
              }
              <Folder className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-gray-300">{file.name}</span>
            </div>
          </div>
        ) : (
          <div 
            className="flex items-center gap-2 px-3 py-1.5 pl-10 hover:bg-white/5 cursor-pointer rounded"
            onClick={() => openFile(file.path)}
          >
            <File className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-300">{file.name}</span>
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className="h-full flex" data-testid="project-workspace">
      {/* File Explorer */}
      <div className="w-64 border-r border-white/10 bg-black/20 overflow-y-auto">
        <div className="p-4 border-b border-white/10">
          <h3 className="text-white font-semibold">{project?.name || 'Project'}</h3>
          <p className="text-xs text-gray-400 mt-1">Files</p>
        </div>
        <div className="p-2">
          {renderFileTree(files)}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Code Editor */}
        {currentFile && (
          <div className="flex-1 border-b border-white/10 flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-black/30">
              <div className="flex items-center gap-2">
                <Code className="w-4 h-4 text-violet-400" />
                <span className="text-sm text-white">{currentFile}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={saveFile}
                  className="text-white hover:bg-white/10"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => setCurrentFile(null)}
                  className="text-white hover:bg-white/10"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <textarea
              value={fileContent}
              onChange={(e) => setFileContent(e.target.value)}
              className="flex-1 bg-gray-950 text-white p-4 font-mono text-sm focus:outline-none resize-none"
              spellCheck={false}
            />
          </div>
        )}

        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-3xl rounded-2xl p-4 ${
                    message.role === 'user'
                      ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white'
                      : 'bg-white/5 text-gray-100 border border-white/10'
                  }`}
                >
                  <div className="prose prose-invert max-w-none text-sm">
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </div>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex gap-3 justify-start">
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                  <Loader2 className="w-5 h-5 text-violet-400 animate-spin" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-white/10 bg-black/30 p-4">
            <div className="flex gap-3 items-end">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Например: 'Создай компонент Header' или 'Исправь баг в api.js'..."
                className="flex-1 bg-white/5 border border-white/20 text-white placeholder-gray-400 rounded-xl p-3 resize-none focus:outline-none focus:ring-2 focus:ring-violet-500"
                rows={3}
                disabled={loading}
              />
              <Button
                onClick={sendMessage}
                disabled={loading || !inputMessage.trim()}
                className="h-12 px-6 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 rounded-xl"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectWorkspace;
